<template>
  <Dialog
    :visible="visible"
    modal
    header="Submit Feedback"
    :style="{ width: '32rem' }"
    :closable="!isSubmitting"
    @update:visible="(val: boolean) => { if (!val) $emit('close') }"
  >
    <p class="feedback-dialog__intro">
      Whether you are reporting a bug or sharing an idea, it helps us to know:
    </p>
    <ul class="feedback-dialog__tips">
      <li><strong>Details:</strong> The specific page, URL, or workflow involved.</li>
      <li><strong>Context:</strong> A quick play-by-play of the issue, or the problem your feature request solves.</li>
      <li><strong>Visuals:</strong> Screenshots, recordings, or examples are always a huge help!</li>
    </ul>

    <div class="feedback-dialog__form">
      <div class="feedback-dialog__field">
        <label for="feedback-summary">Summary of Issue <span class="feedback-dialog__required">*</span></label>
        <InputText
          id="feedback-summary"
          v-model="summary"
          :maxlength="255"
          :disabled="isSubmitting"
          placeholder="Brief summary of the issue or idea"
          fluid
        />
        <small v-if="summaryError" class="feedback-dialog__error">{{ summaryError }}</small>
      </div>

      <div class="feedback-dialog__field">
        <label for="feedback-description">Issue Description <span class="feedback-dialog__required">*</span></label>
        <Textarea
          id="feedback-description"
          v-model="description"
          :maxlength="32000"
          :disabled="isSubmitting"
          auto-resize
          :rows="5"
          placeholder="Describe the issue or feature request in detail"
          fluid
        />
        <small v-if="descriptionError" class="feedback-dialog__error">{{ descriptionError }}</small>
      </div>

      <div class="feedback-dialog__field">
        <label>Attachments <span class="feedback-dialog__optional">(optional)</span></label>
        <FileUpload
          mode="basic"
          :multiple="true"
          :accept="acceptedTypes"
          :max-file-size="10485760"
          :disabled="isSubmitting"
          choose-label="Choose Files"
          :auto="false"
          @select="onFileSelect"
        />
        <div v-if="files.length > 0" class="feedback-dialog__files">
          <div v-for="(file, i) in files" :key="i" class="feedback-dialog__file">
            <span>{{ file.name }} ({{ formatFileSize(file.size) }})</span>
            <Button
              icon="pi pi-times"
              text
              rounded
              severity="secondary"
              size="small"
              :disabled="isSubmitting"
              @click="removeFile(i)"
            />
          </div>
        </div>
        <small v-if="fileError" class="feedback-dialog__error">{{ fileError }}</small>
      </div>
    </div>

    <template #footer>
      <p
        v-if="submitError"
        class="feedback-dialog__submit-error"
      >
        {{ submitError }}
      </p>
      <Button
        label="Cancel"
        text
        :disabled="isSubmitting"
        @click="$emit('close')"
      />
      <Button
        label="Submit"
        :loading="isSubmitting"
        :disabled="!canSubmit || isSubmitting"
        @click="handleSubmit"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import FileUpload from 'primevue/fileupload';
import Button from 'primevue/button';
import type { FileUploadSelectEvent } from 'primevue/fileupload';

const props = defineProps<{
  visible: boolean;
  isSubmitting: boolean;
  canSubmit: boolean;
  submitError?: string | null;
}>();

const emit = defineEmits<{
  close: [];
  submit: [{ summary: string; description: string; files: File[] }];
}>();

const acceptedTypes = 'image/*,.pdf,.txt,.log,.mp4,.mov';
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const summary = ref('');
const description = ref('');
const files = ref<File[]>([]);
const summaryError = ref('');
const descriptionError = ref('');
const fileError = ref('');

function onFileSelect(event: FileUploadSelectEvent) {
  const incoming = Array.from(event.files as File[]);

  if (files.value.length + incoming.length > MAX_FILES) {
    fileError.value = `Maximum ${MAX_FILES} attachments allowed`;
    return;
  }

  for (const f of incoming) {
    if (f.size > MAX_FILE_SIZE) {
      fileError.value = `File "${f.name}" exceeds the 10MB limit`;
      return;
    }
  }

  fileError.value = '';
  files.value.push(...incoming);
}

function removeFile(index: number) {
  files.value.splice(index, 1);
  fileError.value = '';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function validate(): boolean {
  let valid = true;
  summaryError.value = '';
  descriptionError.value = '';

  if (!summary.value.trim()) {
    summaryError.value = 'Summary is required';
    valid = false;
  }
  if (!description.value.trim()) {
    descriptionError.value = 'Description is required';
    valid = false;
  }
  return valid;
}

function handleSubmit() {
  if (!validate()) return;
  emit('submit', {
    summary: summary.value.trim(),
    description: description.value.trim(),
    files: files.value,
  });
}

function resetForm() {
  summary.value = '';
  description.value = '';
  files.value = [];
  summaryError.value = '';
  descriptionError.value = '';
  fileError.value = '';
}

watch(
  () => props.visible,
  (visible) => {
    if (!visible && !props.isSubmitting) resetForm();
  },
);
</script>

<style scoped>
.feedback-dialog__intro {
  color: var(--app-text-muted);
  font-size: var(--app-font-size-body-base);
  margin: 0 0 var(--app-space-8) 0;
}

.feedback-dialog__tips {
  color: var(--app-text-muted);
  font-size: var(--app-font-size-body-small, 0.875rem);
  margin: 0 0 var(--app-space-20) 0;
  padding-left: var(--app-space-20);
}

.feedback-dialog__tips li {
  margin-bottom: var(--app-space-4);
}

.feedback-dialog__form {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-16);
}

.feedback-dialog__field {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-4);
}

.feedback-dialog__field label {
  font-weight: var(--app-font-weight-medium, 500);
  font-size: var(--app-font-size-body-base);
  color: var(--app-text-primary);
}

.feedback-dialog__required {
  color: var(--app-text-danger, #dc2626);
}

.feedback-dialog__optional {
  color: var(--app-text-muted);
  font-weight: normal;
}

.feedback-dialog__error {
  color: var(--app-text-danger, #dc2626);
  font-size: var(--app-font-size-caption);
}

.feedback-dialog__files {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-4);
}

.feedback-dialog__file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--app-space-4) var(--app-space-8);
  background: var(--app-background-elevated);
  border-radius: var(--app-radius-sm);
  font-size: var(--app-font-size-caption);
  color: var(--app-text-primary);
}

.feedback-dialog__submit-error {
  color: var(--app-text-danger, #dc2626);
  font-size: var(--app-font-size-caption);
  flex: 1;
  margin: 0;
}
</style>
